import { useState, useRef, useEffect, useMemo } from 'react'
import styles from './WeeklyView.module.css'
import CalendarWorklogModal from './modals/CalendarWorklogModal'
import WorklogContextMenu from './WorklogContextMenu'
import WorklogDescription from './WorklogDescription'
import useWorklogDragAndDrop from '../hooks/useWorklogDragAndDrop'
// Overnight handling removed - backend now splits worklogs at creation/update time
import { moment, formatHourMinute12Hour, isToday, isSameDay, formatDuration, getSlotHeight } from '../utils/dateUtils'
import { formatTime12Hour } from '../utils/dateUtilsV2'
import { getUserTimezone } from '../utils/timezoneUtils'

export default function WeeklyView({
    weekDates,
    selectedDate,
    worklogs,
    calendarEvents = [],
    isGoogleCalendarVisible = true,
    onWorklogCreate,
    onWorklogUpdate,
    onWorklogDelete,
    onSuggestionAccept = () => { },
    onSuggestionDelete = () => { },
    selectedTeammates = [],
    teammates = [],
    getWorklogColors,
    isAdmin = false,
    currentUser = null,
    user = null, // Add user prop for timezone
    availableProjects = [],
    availableTasks = [],
    apiToken = null,
    hoveredProjectId = null,
    showWeekends = true,
    viewDensity = 'relaxed',
    setIsLoading = () => { },
    setLoadingMessage = () => { }
}) {
    // Get user's timezone for consistent time display - prioritize user profile timezone
    // Use useMemo to recalculate when user.timezone changes
    const userTimezone = useMemo(() => getUserTimezone(user?.timezone), [user?.timezone])

    const [showModal, setShowModal] = useState(false)
    const [pendingWorklog, setPendingWorklog] = useState({ billable: true })
    const [isEditMode, setIsEditMode] = useState(false)
    const [hoveredWorklog, setHoveredWorklog] = useState(null)
    const [tooltipData, setTooltipData] = useState(null)
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })
    const currentTimeLineRef = useRef(null)

    // Context menu state
    const [contextMenu, setContextMenu] = useState({
        isVisible: false,
        position: { x: 0, y: 0 },
        worklog: null
    })

    const gridRef = useRef(null)

    const mentionableUsers = useMemo(() => {
        const list = [...teammates]
        if (currentUser) {
            list.push(currentUser)
        }
        return list
    }, [teammates, currentUser])

    // Use the drag and drop hook
    const {
        dragStart,
        isDragging,
        dragPreview,
        draggedWorklog,
        isDraggingWorklog,
        isAltPressed,
        isShiftPressed,
        isResizing,
        resizeWorklog,
        resizeType,
        handleMouseDown,
        handleWorklogMouseDown,
        handleDoubleClick,
        handleResizeMouseDown,
        getDragPreviewStyle,
        getDragPreviewStyleForExistingWorklog,
        getResizePreviewStyle
    } = useWorklogDragAndDrop(onWorklogUpdate, styles, {
        onNewWorklogDrop: (worklogData) => {
            setPendingWorklog(worklogData)
            setIsEditMode(false)
            setShowModal(true)
        },
        onDoubleClick: (worklogData) => {
            setPendingWorklog(worklogData)
            setIsEditMode(false)
            setShowModal(true)
        },
        onWorklogDuplicate: (worklogData) => {
            // Create a duplicate worklog
            onWorklogCreate(worklogData)
        },
        onWorklogMoveStart: () => {
            setIsLoading(true)
            setLoadingMessage('Moving worklog...')
        },
        onWorklogMoveEnd: () => {
            setIsLoading(false)
        }
    }, currentUser, viewDensity, userTimezone)

    // Context menu handlers
    const handleContextMenu = (event, worklog) => {
        event.preventDefault()
        event.stopPropagation()

        setContextMenu({
            isVisible: true,
            position: { x: event.clientX, y: event.clientY },
            worklog: worklog
        })
    }

    const closeContextMenu = () => {
        setContextMenu({
            isVisible: false,
            position: { x: 0, y: 0 },
            worklog: null
        })
    }

    const handleDuplicate = () => {
        const worklog = contextMenu.worklog
        if (!worklog) return

        const duplicateData = {
            title: worklog.title,
            startTime: worklog.startTime,
            endTime: worklog.endTime,
            date: worklog.date,
            project: worklog.project?.id || worklog.project,
            task: worklog.task?.id || worklog.task,
            userId: worklog.userId,
            billable: worklog.billable,
            type: 'work'
        }

        onWorklogCreate(duplicateData)
    }

    const handleDuplicateForMe = () => {
        const worklog = contextMenu.worklog
        console.log('=== handleDuplicateForMe DEBUG ===')
        console.log('worklog:', worklog)
        console.log('currentUser:', currentUser)
        console.log('currentUser.id:', currentUser?.id)

        if (!worklog || !currentUser?.id) {
            console.log('Early return - missing worklog or currentUser.id')
            return
        }

        const duplicateData = {
            title: worklog.title,
            startTime: worklog.startTime,
            endTime: worklog.endTime,
            date: worklog.date,
            project: worklog.project?.id || worklog.project,
            task: worklog.task?.id || worklog.task,
            userId: currentUser.id,
            billable: worklog.billable,
            type: 'work'
        }

        console.log('duplicateData being sent:', duplicateData)
        console.log('Original worklog userId:', worklog.userId)
        console.log('New worklog userId:', duplicateData.userId)

        onWorklogCreate(duplicateData)
    }

    const handleSplitFromMenu = () => {
        const worklog = contextMenu.worklog
        if (!worklog) return

        handleWorklogSplit(worklog)
    }

    const handleDeleteFromMenu = () => {
        const worklog = contextMenu.worklog
        if (!worklog) return

        handleWorklogDelete(worklog)
    }

    // Handle worklog splitting (Ctrl+click)
    const handleWorklogSplit = async (worklog) => {
        try {
            // Calculate the midpoint time - this ensures first entry's end time = second entry's start time
            const startTime = moment(worklog.startTime)
            const endTime = moment(worklog.endTime)
            const totalDuration = endTime.diff(startTime)
            const halfDuration = totalDuration / 2
            const midTime = moment(startTime).add(halfDuration, 'milliseconds')

            // First half - update the existing worklog (original start → midpoint)
            const firstHalfData = {
                start_time: startTime.toISOString(),
                end_time: midTime.toISOString(), // First entry ends at midTime
                description: worklog.title,
                project: worklog.project?.id || worklog.project,
                task: worklog.task?.id || worklog.task,
                user_id: worklog.userId, // Preserve original user assignment
                billable: worklog.billable
            }

            // Second half - create a new worklog (midpoint → original end)
            const secondHalfData = {
                title: worklog.title,
                startTime: midTime.toDate(), // Second entry starts at midTime (same as first entry's end)
                endTime: endTime.toDate(),
                date: worklog.date,
                project: worklog.project?.id || worklog.project,
                task: worklog.task?.id || worklog.task,
                userId: worklog.userId,
                billable: worklog.billable,
                type: 'work'
            }

            // Execute operations sequentially to avoid state update conflicts
            console.log('=== Starting sequential operations ===')
            const updateResult = await onWorklogUpdate(worklog.id, firstHalfData)
            console.log('=== Update completed, now creating ===')
            const createResult = await onWorklogCreate(secondHalfData)
            console.log('=== Create completed ===')

            // Small delay to ensure state is fully processed
            await new Promise(resolve => setTimeout(resolve, 50))

            console.log('=== SPLIT OPERATION COMPLETED ===')
            console.log('Both API calls finished, waiting for state updates...')

            // Force a delay to ensure React state updates are processed
            await new Promise(resolve => setTimeout(resolve, 200))

            console.log('=== SPLIT OPERATION FINAL ===')
            console.log('State update delay completed')

        } catch (error) {
            console.error('Failed to split worklog:', error)
            alert('Failed to split worklog: ' + error.message)
        }
    }

    // Handle worklog deletion with confirmation
    const handleWorklogDelete = async (worklog) => {
        const startTime = formatTime12Hour(worklog.startTime, userTimezone)
        const endTime = formatTime12Hour(worklog.endTime, userTimezone)
        const confirmMessage = `Are you sure you want to delete this worklog?\n\n"${worklog.title}"\n${startTime} - ${endTime}`

        if (window.confirm(confirmMessage)) {
            try {
                await onWorklogDelete(worklog.id)
                console.log('Worklog deleted successfully')
            } catch (error) {
                console.error('Failed to delete worklog:', error)
                alert('Failed to delete worklog: ' + error.message)
            }
        }
    }

    // Keyboard event handler for delete key
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.key === 'Delete' || e.key === 'Backspace') && hoveredWorklog && !isDraggingWorklog && !showModal) {
                e.preventDefault()
                handleWorklogDelete(hoveredWorklog)
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [hoveredWorklog, isDraggingWorklog, showModal])

    // Update current time indicator position every minute via direct DOM manipulation
    // Uses a ref instead of state to avoid re-rendering the entire component
    useEffect(() => {
        const updateTimeLinePosition = () => {
            if (!currentTimeLineRef.current) return
            const now = moment.tz(moment(), userTimezone)
            const currentMinutes = now.hour() * 60 + now.minute()
            const slotHeight = getSlotHeight(viewDensity)
            const top = (currentMinutes / 15) * slotHeight
            currentTimeLineRef.current.style.top = `${top}px`
        }

        updateTimeLinePosition()
        const interval = setInterval(updateTimeLinePosition, 60000)

        return () => clearInterval(interval)
    }, [userTimezone, viewDensity])

    // Generate time slots (24 hours in 15-minute intervals)
    const generateTimeSlots = () => {
        const slots = []
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 15) {
                const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
                slots.push({ hour, minute, time })
            }
        }
        return slots
    }

    const timeSlots = generateTimeSlots()

    // Track window width changes for responsive grid columns
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024)

    // Handle window resize for responsive grid columns
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth)
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    // Calculate and apply scrollbar width to align grids
    useEffect(() => {
        if (gridRef.current) {
            const timeGrid = gridRef.current.querySelector(`.${styles.timeGrid}`)
            const weekHeader = gridRef.current.querySelector(`.${styles.weekHeader}`)
            const gridLinesOverlay = gridRef.current.querySelector(`.${styles.gridLinesOverlay}`)

            if (timeGrid && weekHeader && gridLinesOverlay) {
                // Calculate scrollbar width
                const scrollbarWidth = timeGrid.offsetWidth - timeGrid.clientWidth

                // Apply scrollbar offset to header and overlay to match timeGrid
                if (scrollbarWidth > 0) {
                    weekHeader.style.paddingRight = `${scrollbarWidth}px`
                    gridLinesOverlay.style.paddingRight = `${scrollbarWidth}px`
                }
            }
        }
    }, [windowWidth, weekDates])

    // Track if we've already scrolled on initial mount
    const hasScrolledRef = useRef(false)

    // Scroll to first worklog or 8:00am on component mount and when week changes
    useEffect(() => {
        // Reset scroll flag at start of effect when week/filters/density changes
        hasScrolledRef.current = false

        const scrollToOptimalPosition = () => {
            console.log('=== SCROLL CALCULATION START ===')
            console.log('gridRef.current:', gridRef.current)
            console.log('worklogs count:', worklogs?.length || 0)
            console.log('weekDates:', weekDates)
            console.log('viewDensity:', viewDensity)

            if (gridRef.current) {
                const slotHeight = getSlotHeight(viewDensity)
                let scrollPosition = 0

                // Find the earliest worklog start time in the week (only for visible worklogs)
                if (worklogs && worklogs.length > 0) {
                    // Filter to only visible worklogs (same logic as in the render)
                    const visibleWorklogs = worklogs.filter(worklog => {
                        // Show worklog if user is selected or if it's current user's worklog and 'self' is selected
                        const isCurrentUserWorklog = worklog.userId === currentUser?.id
                        const isUserSelected = selectedTeammates.includes(worklog.userId)
                        const isSelfSelected = selectedTeammates.includes('self') && isCurrentUserWorklog
                        return isUserSelected || isSelfSelected
                    })
                    console.log('visibleWorklogs count:', visibleWorklogs.length)

                    // Get all start times in minutes from midnight
                    const startTimes = visibleWorklogs.map(worklog => {
                        const startTime = moment(worklog.startTime)
                        const minutes = startTime.hour() * 60 + startTime.minute()
                        console.log(`Visible worklog "${worklog.title}" starts at ${startTime.format('HH:mm')} (${minutes} minutes)`)
                        return minutes
                    })

                    if (startTimes.length > 0) {
                        // Find the earliest start time
                        const earliestMinutes = Math.min(...startTimes)
                        console.log('Earliest worklog time:', earliestMinutes, 'minutes from midnight')

                        // Add some padding (scroll up a bit before the first entry)
                        const paddingMinutes = 30 // Show 30 minutes before first entry
                        const targetMinutes = Math.max(0, earliestMinutes - paddingMinutes)
                        console.log('Target scroll time (with padding):', targetMinutes, 'minutes from midnight')

                        // Calculate scroll position (4 slots per hour, each slot is slotHeight)
                        scrollPosition = (targetMinutes / 15) * slotHeight - 10
                        console.log('Calculated scroll position:', scrollPosition, 'pixels')
                    } else {
                        // Fallback to 8:00am if no worklogs
                        console.log('No start times found, falling back to 8:00 AM')
                        const eightAMSlotIndex = 8 * 4
                        scrollPosition = eightAMSlotIndex * slotHeight - 5
                        console.log('Fallback scroll position (8 AM):', scrollPosition, 'pixels')
                    }
                } else {
                    // Fallback to 8:00am if no worklogs
                    console.log('No worklogs found, falling back to 8:00 AM')
                    const eightAMSlotIndex = 8 * 4
                    scrollPosition = eightAMSlotIndex * slotHeight - 5
                    console.log('Fallback scroll position (8 AM):', scrollPosition, 'pixels')
                }

                // Find the scrollable container (timeGrid)
                const timeGrid = gridRef.current.querySelector(`.${styles.timeGrid}`)
                console.log('timeGrid element found:', !!timeGrid)
                if (timeGrid) {
                    const finalScrollPosition = Math.max(0, scrollPosition)
                    console.log('Setting scrollTop to:', finalScrollPosition)
                    timeGrid.scrollTop = finalScrollPosition
                    console.log('Actual scrollTop after setting:', timeGrid.scrollTop)
                }

                console.log('=== SCROLL CALCULATION END ===')
            }
        }

        // Prevent browser scroll restoration from interfering
        if (window.history?.scrollRestoration) {
            window.history.scrollRestoration = 'manual'
        }

        // Schedule scroll after DOM is rendered
        console.log('Scheduling scroll...')
        const frameId = requestAnimationFrame(scrollToOptimalPosition)
        return () => cancelAnimationFrame(frameId)
    }, [weekDates, viewDensity, selectedTeammates, worklogs])

    // Get user colors for worklog display
    const getUserColors = (userId) => {
        if (userId === currentUser?.id) {
            return getWorklogColors('self')
        }
        return getWorklogColors(userId)
    }

    // Helper function to get teammate display name
    const getTeammateDisplayName = (userId) => {
        if (userId === currentUser?.id) return null // Don't show name for current user
        const teammate = teammates.find(t => t.id === userId)
        return teammate?.display_name || teammate?.first_name || 'Unknown User'
    }



    // Using centralized formatTime12Hour from dateUtils

    // Calculate daily total for a specific date (only for visible teammates)
    const calculateDailyTotal = (date) => {
        // Filter by date first
        const dayWorklogs = worklogs.filter(worklog => isSameDay(worklog.date, date))

        // Then filter by selected teammates (same logic as in worklog display)
        const visibleDayWorklogs = dayWorklogs.filter(worklog => {
            const isCurrentUserWorklog = worklog.userId === currentUser?.id
            const isUserSelected = selectedTeammates.includes(worklog.userId)
            const isSelfSelected = selectedTeammates.includes('self') && isCurrentUserWorklog

            return isUserSelected || isSelfSelected
        })

        const totalMinutes = visibleDayWorklogs.reduce((total, worklog) => {
            const duration = moment(worklog.endTime).diff(moment(worklog.startTime), 'minutes')
            return total + duration
        }, 0)

        return formatDuration(totalMinutes * 60) // formatDuration expects seconds
    }

    const formatDateHeader = (date) => {
        const isTodayDate = isToday(date)
        const dailyTotal = calculateDailyTotal(date)

        return (
            <div className={`${styles.dateHeader} ${isTodayDate ? styles.today : ''}`}>
                <div className={styles.dayName}>
                    {moment(date).format('ddd')}
                </div>
                <div className={styles.dayNumber}>
                    {moment(date).format('D')}
                </div>
                {dailyTotal && (
                    <div className={styles.dailyTotal}>
                        {dailyTotal}
                    </div>
                )}
            </div>
        )
    }

    // Detect overlapping worklogs and calculate positioning
    const getOverlapInfo = (targetWorklog, dayWorklogs) => {
        // Extract hour/minute in user's timezone for correct overlap detection
        const targetStart = moment.tz(targetWorklog.startTime, userTimezone).hour() * 60 + moment.tz(targetWorklog.startTime, userTimezone).minute()
        const targetEnd = moment.tz(targetWorklog.endTime, userTimezone).hour() * 60 + moment.tz(targetWorklog.endTime, userTimezone).minute()

        // Find all overlapping worklogs (including the target)
        const overlapping = dayWorklogs.filter(worklog => {
            const start = moment.tz(worklog.startTime, userTimezone).hour() * 60 + moment.tz(worklog.startTime, userTimezone).minute()
            const end = moment.tz(worklog.endTime, userTimezone).hour() * 60 + moment.tz(worklog.endTime, userTimezone).minute()

            // Check if there's any time overlap
            return start < targetEnd && end > targetStart
        })

        // Sort by start time, then by id for consistent positioning
        overlapping.sort((a, b) => {
            const aStart = moment.tz(a.startTime, userTimezone).hour() * 60 + moment.tz(a.startTime, userTimezone).minute()
            const bStart = moment.tz(b.startTime, userTimezone).hour() * 60 + moment.tz(b.startTime, userTimezone).minute()
            if (aStart !== bStart) return aStart - bStart
            return a.id - b.id
        })

        const totalColumns = overlapping.length
        const columnIndex = overlapping.findIndex(w => w.id === targetWorklog.id)

        return { totalColumns, columnIndex }
    }

    const getWorklogStyle = (worklog, dayWorklogs) => {
        // Extract hour/minute in user's timezone for correct grid positioning
        const startMinutes = moment.tz(worklog.startTime, userTimezone).hour() * 60 + moment.tz(worklog.startTime, userTimezone).minute()
        const endMinutes = moment.tz(worklog.endTime, userTimezone).hour() * 60 + moment.tz(worklog.endTime, userTimezone).minute()

        // Each time slot is 15 minutes, grid row height is calculated accordingly
        const slotHeight = getSlotHeight(viewDensity)
        const maxMinutes = 24 * 60 // 1440 minutes in a day

        // Cap the end time to end of day to prevent overflow
        const cappedEndMinutes = Math.min(endMinutes, maxMinutes)
        const duration = cappedEndMinutes - startMinutes

        const top = (startMinutes / 15) * slotHeight
        const height = (duration / 15) * slotHeight

        // Calculate overlap positioning
        const { totalColumns, columnIndex } = getOverlapInfo(worklog, dayWorklogs)

        // Calculate width and position based on overlaps
        const availableWidth = 100 // percentage
        const columnWidth = availableWidth / totalColumns
        const leftPercent = columnIndex * columnWidth

        // Get colors based on user
        const worklogColors = getUserColors(worklog.userId)

        // Use project color for border if available, otherwise fall back to user color
        const borderColor = worklog.project?.color || worklogColors.borderColor

        // Set border style based on billable status - dotted for non-billable, solid for billable
        const borderStyle = worklog.billable === false ? 'dotted' : 'solid'

        // Apply dimming effect when a project is hovered and this worklog is not from that project
        const isDimmed = hoveredProjectId && hoveredProjectId !== (worklog.project?.id || 'general')
        const opacity = isDimmed ? 0.3 : 1
        const filter = isDimmed ? 'brightness(0.5) grayscale(0.5)' : 'brightness(0.95)'

        return {
            top: `${top}px`,
            height: `${height}px`,
            left: `${leftPercent}%`,
            width: `${columnWidth}%`,
            paddingRight: '2px',
            backgroundColor: worklogColors.cardColor,
            borderLeft: `10px ${borderStyle} ${borderColor}`,
            color: worklogColors.textColor,
            opacity: opacity,
            filter: filter,
            transition: 'opacity 0.2s ease, filter 0.2s ease'
        }
    }


    const numDays = weekDates.length;

    // Responsive grid columns based on screen size
    const getGridColumns = () => {
        if (windowWidth <= 768) {
            return `50px repeat(${numDays}, 1fr)`;
        } else if (windowWidth <= 1024) {
            return `60px repeat(${numDays}, 1fr)`;
        }
        return `80px repeat(${numDays}, 1fr)`;
    };

    const gridColumns = getGridColumns();

    const renderSuggestionCard = (worklog, worklogStyle, shouldHideTime) => {
        const suggestedByName = worklog.suggestedBy?.name || worklog.suggestedBy?.username || 'Teammate'

        return (
            <div
                key={worklog.id}
                className={`${styles.worklog} ${styles.suggestion} ${styles.suggestionCard}`}
                style={worklogStyle}
            >
                <div className={styles.suggestionBadge}>
                    Suggested by {suggestedByName}
                </div>
                <WorklogDescription
                    description={worklog.title}
                    className={styles.worklogTitle}
                    mentionableUsers={mentionableUsers}
                />
                <div className={`${styles.worklogTime} ${shouldHideTime ? styles.hideTime : ''}`}>
                    {formatTime12Hour(worklog.startTime, userTimezone)} - {formatTime12Hour(worklog.endTime, userTimezone)}
                </div>
                {worklog.project?.name && (
                    <div className={styles.projectTaskInfo}>
                        {worklog.project.name}
                        {worklog.task && ` > ${worklog.task.name || 'Task'}`}
                    </div>
                )}
                <div className={styles.suggestionActions}>
                    <button
                        className={styles.suggestionAccept}
                        onClick={(event) => {
                            event.stopPropagation()
                            onSuggestionAccept(worklog)
                        }}
                        title="Accept suggestion"
                        aria-label="Accept suggestion"
                    >
                        ✓
                    </button>
                    <button
                        className={styles.suggestionDelete}
                        onClick={(event) => {
                            event.stopPropagation()
                            onSuggestionDelete(worklog)
                        }}
                        title="Decline suggestion"
                        aria-label="Decline suggestion"
                    >
                        ✗
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.weeklyView} ref={gridRef}>
            {/* Vertical grid lines overlay */}
            <div
                className={styles.gridLinesOverlay}
                style={{ gridTemplateColumns: gridColumns }}
            >
                {/* Create vertical lines for each column boundary */}
                {Array.from({ length: numDays + 1 }, (_, index) => (
                    <div key={index} className={styles.verticalLine} />
                ))}
            </div>

            {/* Week header with dates */}
            <div
                className={styles.weekHeader}
                style={{ gridTemplateColumns: gridColumns }}
            >
                <div className={styles.timeColumn}>
                    {/* Empty space above time column */}
                </div>
                {weekDates.map((date, index) => {
                    const isWeekend = moment(date).day() === 0 || moment(date).day() === 6 // Sunday or Saturday
                    return (
                        <div key={index} className={`${styles.dateColumn} ${isWeekend ? styles.weekend : ''}`}>
                            {formatDateHeader(date)}
                        </div>
                    )
                })}
            </div>

            {/* Time grid */}
            <div
                className={styles.timeGrid}
                style={{
                    gridTemplateColumns: gridColumns,
                    '--calendar-slot-height': viewDensity === 'relaxed' ? '20px' : '16px'
                }}
            >
                {/* Time labels column */}
                <div className={styles.timeLabels}>
                    {timeSlots.map((slot, index) => (
                        <div key={index} className={styles.timeSlot}>
                            {slot.minute === 0 && slot.hour !== 0 && (
                                <span className={styles.timeLabel}>
                                    {formatHourMinute12Hour(slot.hour, slot.minute)}
                                </span>
                            )}
                        </div>
                    ))}
                </div>

                {/* Days columns */}
                {weekDates.map((date, dayIndex) => {
                    const isWeekend = moment(date).day() === 0 || moment(date).day() === 6 // Sunday or Saturday
                    const dayCalendarEvents = calendarEvents.filter(event => {
                        const eventStart = moment(event.startTime).startOf('day');
                        const eventEnd = moment(event.endTime).endOf('day');
                        const currentDay = moment(date).startOf('day');
                        // Show event if the current day is between the event's start and end, inclusive.
                        return currentDay.isSameOrAfter(eventStart) && currentDay.isSameOrBefore(eventEnd);
                    });

                    return (
                        <div key={dayIndex} className={`${styles.dayColumn} ${isWeekend ? styles.weekend : ''}`}>
                            {/* Calendar Events Lane (Clockify-style) - Only render if Google Calendar is visible */}
                            {isGoogleCalendarVisible && (
                                <div className={styles.calendarEventsLane}>
                                    {dayCalendarEvents.map(event => {
                                        const slotHeight = getSlotHeight(viewDensity);
                                        const startMoment = moment(event.startTime);
                                        const endMoment = moment(event.endTime);

                                        const startMinutes = startMoment.hour() * 60 + startMoment.minute();
                                        const durationMinutes = endMoment.diff(startMoment, 'minutes');

                                        const top = (startMinutes / 15) * slotHeight;
                                        const height = (durationMinutes / 15) * slotHeight;
                                        const isAllDay = event.allDay || durationMinutes >= (23 * 60);

                                        return (
                                            <div
                                                key={event.id}
                                                className={styles.calendarEvent}
                                                style={{
                                                    top: `${top}px`,
                                                    height: `${height}px`,
                                                }}
                                                onClick={() => {
                                                    const worklogData = {
                                                        title: event.description || event.summary,
                                                        startTime: event.startTime,
                                                        endTime: event.endTime,
                                                        date: moment(event.startTime).startOf('day').toDate(),
                                                        billable: true,
                                                        type: 'work'
                                                    };
                                                    setPendingWorklog(worklogData);
                                                    setIsEditMode(false);
                                                    setShowModal(true);
                                                }}
                                                title={`${event.summary}\n${isAllDay ? 'All day' : `${formatTime12Hour(event.startTime, userTimezone)} - ${formatTime12Hour(event.endTime, userTimezone)}`}`}
                                            >
                                                <div className={styles.calendarEventTitle}>
                                                    {event.summary}
                                                </div>
                                                {!isAllDay && height > 20 && (
                                                    <div className={styles.calendarEventTime}>
                                                        {formatTime12Hour(event.startTime, userTimezone)}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Time Slots & Worklogs Grid */}
                            <div className={styles.timeSlotsGrid}>
                                {/* Time slots for this day */}
                                {timeSlots.map((slot, slotIndex) => (
                                    <div
                                        key={slotIndex}
                                        className={styles.timeSlot}
                                        data-slot={`${slot.hour}:${slot.minute}`}
                                        data-date={moment(date).toISOString()}
                                        onMouseDown={(e) => handleMouseDown(e, date, slot)}
                                        onDoubleClick={(e) => handleDoubleClick(e, date, slot)}
                                    />
                                ))}

                                {/* Worklogs for this day */}
                                {(() => {
                                    // Filter by date and selected teammates
                                    const dayWorklogs = worklogs.filter(worklog => {
                                        const isOnDate = isSameDay(worklog.date, date)
                                        if (!isOnDate) return false

                                        // Show worklog if user is selected or if it's current user's worklog and 'self' is selected
                                        const isCurrentUserWorklog = worklog.userId === currentUser?.id
                                        const isUserSelected = selectedTeammates.includes(worklog.userId)
                                        const isSelfSelected = selectedTeammates.includes('self') && isCurrentUserWorklog

                                        return isUserSelected || isSelfSelected
                                    })
                                    return dayWorklogs.map(worklog => {
                                        // Hide the original worklog if it's being moved (not duplicated) to a different date
                                        const isBeingMovedElsewhere = isDraggingWorklog && draggedWorklog?.id === worklog.id &&
                                            !isSameDay(draggedWorklog.date, date) && !isAltPressed

                                        if (isBeingMovedElsewhere) {
                                            return null // Don't render the worklog in its original position when moving
                                        }

                                        // For duplication mode, show original worklog in original position
                                        const isBeingDuplicated = isDraggingWorklog && draggedWorklog?.id === worklog.id && isAltPressed
                                        const isBeingMoved = isDraggingWorklog && draggedWorklog?.id === worklog.id && !isAltPressed

                                        // Calculate worklog height in pixels
                                        const worklogStyle = isBeingMoved ? getWorklogStyle(draggedWorklog, dayWorklogs) : getWorklogStyle(worklog, dayWorklogs)
                                        const worklogHeight = parseFloat(worklogStyle.height)

                                        // Determine which elements to hide based on height
                                        const shouldHideFooter = worklogHeight < 72
                                        const shouldHideTime = worklogHeight < 48

                                        const isSuggestion = worklog.type === 'suggestion'

                                        if (isSuggestion) {
                                            return renderSuggestionCard(worklog, worklogStyle, shouldHideTime)
                                        }

                                        return (
                                            <div
                                                key={worklog.id}
                                                className={`${styles.worklog} ${styles[worklog.type]} ${isBeingMoved ? styles.dragging : ''} ${isBeingDuplicated ? styles.duplicating : ''}`}
                                                style={worklogStyle}
                                                onMouseDown={(e) => handleWorklogMouseDown(e, worklog)}
                                                onMouseEnter={(e) => {
                                                    setHoveredWorklog(worklog)
                                                    const rect = e.currentTarget.getBoundingClientRect()
                                                    setTooltipData({
                                                        title: worklog.title,
                                                        startTime: formatTime12Hour(worklog.startTime, userTimezone),
                                                        endTime: formatTime12Hour(worklog.endTime, userTimezone),
                                                        duration: formatDuration(moment(worklog.endTime).diff(moment(worklog.startTime), 'seconds')),
                                                        project: worklog.project?.name || 'No project',
                                                        task: worklog.task ? (typeof worklog.task === 'object' ? (worklog.task.name || 'Task') : worklog.task) : null
                                                    })
                                                    setTooltipPosition({
                                                        x: rect.left + rect.width / 2,
                                                        y: rect.top - 10
                                                    })
                                                }}
                                                onMouseLeave={() => {
                                                    setHoveredWorklog(null)
                                                    setTooltipData(null)
                                                }}
                                                onContextMenu={(e) => handleContextMenu(e, worklog)}
                                                onClick={(e) => {
                                                    // Only handle click if not dragging
                                                    if (!isDraggingWorklog) {
                                                        if (e.altKey && e.shiftKey) {
                                                            // Shift+Option+click - duplicate for me
                                                            setContextMenu(prev => ({ ...prev, worklog }))
                                                            handleDuplicateForMe()
                                                        } else if (e.altKey && (e.ctrlKey || e.metaKey)) {
                                                            // Option+Cmd+click - duplicate for me
                                                            setContextMenu(prev => ({ ...prev, worklog }))
                                                            handleDuplicateForMe()
                                                        } else if (e.altKey) {
                                                            // Option+click - duplicate the worklog
                                                            setContextMenu(prev => ({ ...prev, worklog }))
                                                            handleDuplicate()
                                                        } else if (e.ctrlKey || e.metaKey) {
                                                            // Ctrl+click (or Cmd+click on Mac) - split the worklog
                                                            handleWorklogSplit(worklog)
                                                        } else {
                                                            // Normal click - edit the worklog
                                                            setPendingWorklog(worklog)
                                                            setIsEditMode(true)
                                                            setShowModal(true)
                                                        }
                                                    }
                                                }}
                                            >
                                                <WorklogDescription
                                                    description={worklog.title}
                                                    className={styles.worklogTitle}
                                                    mentionableUsers={mentionableUsers}
                                                />
                                                <div className={`${styles.worklogTime} ${shouldHideTime ? styles.hideTime : ''}`}>
                                                    {formatTime12Hour(worklog.startTime, userTimezone)} - {formatTime12Hour(worklog.endTime, userTimezone)}
                                                </div>
                                                <div className={`${styles.worklogFooter} ${shouldHideFooter ? styles.hideFooter : ''}`}>
                                                    <div className={styles.projectTaskInfo}>
                                                        {worklog.project?.name && (
                                                            <span>
                                                                {worklog.project.name}
                                                                {worklog.task && ` > ${typeof worklog.task === 'object' ? (worklog.task.name || 'Task') : worklog.task}`}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Resize handles */}
                                                <div
                                                    className={styles.resizeHandleTop}
                                                    onMouseDown={(e) => handleResizeMouseDown(e, worklog, 'top')}
                                                />
                                                <div
                                                    className={styles.resizeHandleBottom}
                                                    onMouseDown={(e) => handleResizeMouseDown(e, worklog, 'bottom')}
                                                />
                                            </div>
                                        )
                                    }).filter(Boolean) // Remove null entries
                                })()}

                                {/* Drag preview for new worklog creation */}
                                {isDragging && dragPreview && isSameDay(dragPreview.date, date) && (
                                    <div
                                        className={`${styles.worklog} ${styles.preview}`}
                                        style={getDragPreviewStyle()}
                                    >
                                        <div className={styles.worklogTitle}></div>
                                        <div className={styles.worklogTime}>
                                            {formatTime12Hour(dragPreview.startTime, userTimezone)} - {formatTime12Hour(dragPreview.endTime, userTimezone)}
                                        </div>
                                        <div className={styles.worklogFooter}>
                                            <div className={styles.projectTaskInfo}>
                                                <span>Select project</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Drag preview for existing worklog being moved to this date */}
                                {isDraggingWorklog && draggedWorklog && isSameDay(draggedWorklog.date, date) && (
                                    <div
                                        className={`${styles.worklog} ${styles.preview} ${styles[draggedWorklog.type]}`}
                                        style={getDragPreviewStyleForExistingWorklog()}
                                    >
                                        <WorklogDescription
                                            description={draggedWorklog.title}
                                            className={styles.worklogTitle}
                                            mentionableUsers={mentionableUsers}
                                        />
                                        {getTeammateDisplayName(draggedWorklog.userId) && (
                                            <div className={styles.worklogUser}>
                                                {getTeammateDisplayName(draggedWorklog.userId)}
                                            </div>
                                        )}
                                        <div className={styles.worklogTime}>
                                            {formatTime12Hour(draggedWorklog.startTime, userTimezone)} - {formatTime12Hour(draggedWorklog.endTime, userTimezone)}
                                        </div>
                                    </div>
                                )}

                                {/* Resize preview */}
                                {isResizing && resizeWorklog && isSameDay(resizeWorklog.date, date) && (
                                    <div
                                        className={`${styles.worklog} ${styles.resizePreview}`}
                                        style={getResizePreviewStyle()}
                                    >
                                        <WorklogDescription
                                            description={resizeWorklog.title}
                                            className={styles.worklogTitle}
                                            mentionableUsers={mentionableUsers}
                                        />
                                        <div className={styles.worklogTime}>
                                            {formatTime12Hour(resizeWorklog.startTime, userTimezone)} - {formatTime12Hour(resizeWorklog.endTime, userTimezone)}
                                        </div>
                                    </div>
                                )}

                            </div>
                        </div>
                    )
                })}

                {/* Current Time Indicator - spans across all day columns */}
                <div
                    ref={currentTimeLineRef}
                    className={styles.currentTimeLine}
                />
            </div>

            {/* Add Worklog Modal */}
            <CalendarWorklogModal
                isOpen={showModal}
                onClose={() => {
                    setShowModal(false)
                    setPendingWorklog(null)
                    setIsEditMode(false)
                }}
                onSave={(worklogData) => {
                    if (isEditMode) {
                        onWorklogUpdate(worklogData.id, worklogData)
                    } else {
                        onWorklogCreate(worklogData)
                    }
                    setPendingWorklog(null)
                    setIsEditMode(false)
                }}
                onDelete={(worklogId) => {
                    onWorklogDelete(worklogId)
                    setPendingWorklog(null)
                    setIsEditMode(false)
                }}
                initialData={pendingWorklog}
                isEditMode={isEditMode}
                teammates={teammates}
                isAdmin={isAdmin}
                currentUser={currentUser}
                availableProjects={availableProjects}
                availableTasks={availableTasks}
                apiToken={apiToken}
            />

            {/* Context Menu */}
            <WorklogContextMenu
                isVisible={contextMenu.isVisible}
                position={contextMenu.position}
                onClose={closeContextMenu}
                onDuplicate={handleDuplicate}
                onDuplicateForMe={handleDuplicateForMe}
                onSplit={handleSplitFromMenu}
                onDelete={handleDeleteFromMenu}
                worklog={contextMenu.worklog}
            />

            {/* Custom Tooltip */}
            {tooltipData && (
                <div
                    className={styles.worklogTooltip}
                    style={{
                        left: `${tooltipPosition.x}px`,
                        top: `${tooltipPosition.y}px`,
                        transform: 'translate(-50%, -100%)'
                    }}
                >
                    <div className={styles.tooltipTitle}>{tooltipData.title}</div>
                    <div className={styles.tooltipTime}>
                        {tooltipData.startTime} - {tooltipData.endTime}
                        <span className={styles.tooltipDuration}> ({tooltipData.duration})</span>
                    </div>
                    <div className={styles.tooltipProject}>
                        {tooltipData.project}
                        {tooltipData.task && ` > ${tooltipData.task}`}
                    </div>
                </div>
            )}
        </div>
    )
}
